/**
 * 登录页（6d-1 密码登录；6d-2 验证码登录+区号+防水墙+协议链接；6e 2FA+忘记密码；
 * 6f 扫码登录）。首登设密码是独立路由（6e-2）。
 *
 * 两端共用一套 DOM：移动全屏、桌面居中卡片（CSS max-width 收窄，无 isMobile 分支）。
 * 登录成功不在这里导航——守卫（nav/guard.ts）看到 logged-in 自动跳回原页。
 *
 * 2FA / 忘记密码是卡片内子视图（非独立路由：刷新回登录表单重来，守卫零改动）；
 * 验证码登录（默认 Tab，对齐旧版）：同一输入框手机号/邮箱自动识别（含 @ 判邮箱，
 * 邮箱时隐藏区号）；发码前必过防水墙（CaptchaDialog）；账号不存在自动注册。
 * **邮箱登录 Tab（21t，人工要求）**：与验证码 Tab 同一条发码/登录链（后端也是
 * 同一对接口，邮箱只是 `classifyCodeAccount` 分出来的另一种 target），差别只在
 * 「只收邮箱、不显区号、输错报邮箱格式」——所以共用表单不另写一套。
 * 两个 Tab **各存各的账号**：共用 state 的话，在验证码页输了手机号再切过来，
 * 邮箱框里会躺着一串数字然后报格式错误。
 */
import { useState } from 'preact/hooks';
import { markOnce, PERF_MARKS } from '~/perf/marks';
import styles from './login.module.scss';
import { CaptchaDialog } from './CaptchaDialog';
import type { CaptchaResult } from './captcha';
import { COUNTRY_CODES } from './countryCodes';
import { ForgotPasswordView } from './ForgotPasswordView';
import { QrCodeLoginView } from './QrCodeLoginView';
import { TwoFactorView } from './TwoFactorView';
import { useCountdown } from './useCountdown';
import { appWorker } from '~/store/appWorker';
import { globalStore } from '~/store/global';
import {
  classifyCodeAccount,
  loginErrorMessage,
  loginWithCode,
  loginWithPassword,
  sendLoginCode,
  type LoginCommit,
  type LoginOutcome,
  type PendingLogin,
} from '~/store/authActions';
import { useGlobal } from '~/store/useGlobal';
import { ROUTE_ROW_LABEL } from './routeSettingsModel';
import { useRouteSwitch } from './useRouteSwitch';
import { DEFAULT_API_BASE_URL } from '~/util/apiBase';
import { BRAND_NAME } from '~/brand';
import { BackButton } from '~/ui/BackButton';

export interface LoginPageProps {
  /**
   * 添加账号模式（14.34b-2）：已登录态复用登录卡片（路由 add-account，守卫放行）。
   * 登录成功不直写 auth（跳过切片重置路径），改走 commit= 切换收尾
   * （accountsActions.settleAccountSwitch）；卡片带返回钮；扫码登录入口隐藏
   * （刻意收窄——被扫端轮询的 confirmed 落库在 Worker 已收尾旧账号，但主线程
   * commit 覆盖要穿透轮询层，复杂度不值当，验证码/密码/2FA 三路已够用）。
   */
  addAccount?: {
    onBack: () => void;
    commit: LoginCommit;
  };
}

export function LoginPage(props: LoginPageProps = {}) {
  const lostReason = useGlobal((s) =>
    s.auth.status === 'logged-out' ? s.auth.lostReason : undefined,
  );
  // 协议直链跟随当前路线（14.39）：切到海外路线后不能还指向国内域名。
  // 切片未回包（Worker 刚拉起的几毫秒）时按默认路线占位。
  const apiBase = useGlobal((s) => s.netRoute?.baseUrl ?? DEFAULT_API_BASE_URL);
  // 海外路线开关（14.39b）：与路线设置页同一份逻辑（不乐观写、切换中禁用）
  const route = useRouteSwitch();
  const [view, setView] = useState<'login' | '2fa' | 'forgot' | 'qr'>('login');
  /** Need2FA 暂存的原登录参数（view='2fa' 时非 undefined） */
  const [pendingLogin, setPendingLogin] = useState<PendingLogin>();
  /** 子流程完成提示（如「密码已重置」），有错误/切 Tab 时清掉 */
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState<'code' | 'email' | 'password'>('code');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // 密码 Tab
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  // 验证码 Tab
  const [codeAccount, setCodeAccount] = useState('');
  // 邮箱 Tab（21t）：与验证码 Tab **各存各的账号**——共用一个 state 的话，
  // 在验证码页输了手机号再切过来，邮箱框里会躺着一串数字然后报「邮箱格式错误」
  const [emailAccount, setEmailAccount] = useState('');
  const [countryCode, setCountryCode] = useState(86);
  const [smsCode, setSmsCode] = useState('');
  const countdown = useCountdown();
  /** 非空 = 防水墙弹层打开（值 = 解密后的 AppId） */
  const [captchaAppId, setCaptchaAppId] = useState('');

  /** 邮箱 Tab：只收邮箱、不显区号 */
  const emailOnly = tab === 'email';
  const acct = emailOnly ? emailAccount : codeAccount;
  const setAcct = emailOnly ? setEmailAccount : setCodeAccount;
  const isEmail = emailOnly || codeAccount.includes('@');

  function switchTab(next: 'code' | 'email' | 'password') {
    setTab(next);
    setError('');
    setNotice('');
  }

  /** 登录提交的共同收尾：Need2FA 切 2FA 视图，错误展示 */
  function settleLogin(outcome: LoginOutcome) {
    if (outcome.kind === 'need2fa') {
      setPendingLogin(outcome.pending);
      setView('2fa');
      setError('');
    } else if (outcome.kind === 'error') {
      setError(outcome.message);
    }
  }

  async function requestCode() {
    if (busy || countdown.seconds > 0) return;
    const target = classifyCodeAccount(acct, countryCode, emailOnly);
    if (target.kind === 'invalid') {
      setError(target.message);
      return;
    }
    setError('');
    setBusy(true);
    try {
      // config 缓存优先（通常零请求）；发码请求本身在 Worker 内先 ensureIpAnchor
      const config = await appWorker().call('config/get', null);
      setCaptchaAppId(config.captchaAppId);
    } catch (err) {
      setError(loginErrorMessage(err, '验证码发送失败，请稍后重试'));
    } finally {
      setBusy(false);
    }
  }

  async function onCaptchaDone(result: CaptchaResult | undefined) {
    setCaptchaAppId('');
    if (result === undefined) return; // 用户取消：不弹错误（旧版口径）
    const target = classifyCodeAccount(acct, countryCode, emailOnly);
    if (target.kind === 'invalid') return;
    setBusy(true);
    const sent = await sendLoginCode(appWorker(), target, result);
    setBusy(false);
    if (sent.ok) countdown.start(60);
    else setError(sent.message);
  }

  async function submitCode(event: Event) {
    event.preventDefault();
    if (busy) return;
    const target = classifyCodeAccount(acct, countryCode, emailOnly);
    if (target.kind === 'invalid') {
      setError(target.message);
      return;
    }
    if (smsCode.trim().length < 4) {
      setError('请输入验证码');
      return;
    }
    setBusy(true);
    setError('');
    const outcome = await loginWithCode(
      appWorker(),
      globalStore(),
      target,
      smsCode.trim(),
      props.addAccount?.commit,
    );
    setBusy(false);
    settleLogin(outcome);
  }

  async function submitPassword(event: Event) {
    event.preventDefault();
    if (busy || account.trim() === '' || password === '') return;
    setBusy(true);
    setError('');
    const outcome = await loginWithPassword(
      appWorker(),
      globalStore(),
      { account, password },
      props.addAccount?.commit,
    );
    setBusy(false);
    settleLogin(outcome);
  }

  // 「可交互」打点（20a）：未登录冷启动的首屏主内容是登录页（加账号页是二级页，不算首屏）
  if (!props.addAccount) markOnce(PERF_MARKS.interactive);

  return (
    <div
      className={styles.loginPage}
      data-testid={props.addAccount ? 'page-add-account' : 'page-login'}
    >
      {/* 桌面品牌区（21aj，人工给图）：**纯 CSS 分叉**，窄屏 display:none。
          这个文件的既定原则是「两端共用一套 DOM，不加 isMobile 分支」——走 JS
          分支就得把 layout 切片引进登录页，而登录页是未登录冷启动的首屏。
          加账号页不出：那是二级页，摆一遍品牌墙很怪。 */}
      {!props.addAccount && (
        <aside className={styles.hero} data-testid="login-hero">
          <div className={styles.heroBrand}>
            <img className={styles.heroLogo} src="/icons/icon-192.png" alt="" />
            <div>
              <p className={styles.heroName}>{BRAND_NAME}</p>
              <p className={styles.heroPlatform}>Web / 桌面端</p>
            </div>
          </div>
          <h2 className={styles.heroTitle}>快速登录，继续处理你的消息。</h2>
          <p className={styles.heroText}>
            桌面端适合长时间会话、文件拖拽发送和多窗口预览。登录后自动进入主界面。
          </p>
          <ul className={styles.heroPills}>
            <li>回车即发</li>
            <li>拖拽传文件</li>
            <li>桌面通知</li>
          </ul>
        </aside>
      )}
      <div className={styles.card}>
        {/* 21ah-6：这里原来也是一份手写的「‹ 返回」，一并收口到共用 BackButton
            （只剩箭头，全站同形） */}
        {props.addAccount && (
          <BackButton testid="addacc-back" onClick={props.addAccount.onBack} />
        )}
        <h1 className={styles.brand}>{props.addAccount ? '添加账号' : BRAND_NAME}</h1>
        {/* 桌面双栏下品牌名已在左栏，卡片里再写一遍是重复——换成「在做什么」。
            两个标题同时存在、靠 CSS 互斥显示：避免为排版把 layout 切片引进首屏。 */}
        {!props.addAccount && (
          <div className={styles.cardHead}>
            <p className={styles.cardTitle}>登录账号</p>
            <p className={styles.cardHint}>手机号、邮箱或密码，任选一种</p>
          </div>
        )}
        {view === '2fa' && pendingLogin !== undefined ? (
          <TwoFactorView
            pending={pendingLogin}
            commit={props.addAccount?.commit}
            onBack={() => {
              setView('login');
              setPendingLogin(undefined);
            }}
          />
        ) : view === 'qr' ? (
          <QrCodeLoginView onBack={() => setView('login')} />
        ) : view === 'forgot' ? (
          <ForgotPasswordView
            onDone={(message) => {
              setView('login');
              setTab('password');
              setPassword('');
              setError('');
              setNotice(message);
            }}
            onBack={() => setView('login')}
          />
        ) : (
          <>
        {lostReason !== undefined && error === '' && notice === '' && (
          <p className={styles.notice} data-testid="login-expired-notice">
            登录已过期，请重新登录
          </p>
        )}
        {notice !== '' && (
          <p className={styles.notice} data-testid="login-notice">
            {notice}
          </p>
        )}
        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'code'}
            className={tab === 'code' ? styles.tabActive : styles.tab}
            data-testid="login-tab-code"
            onClick={() => switchTab('code')}
          >
            验证码登录
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'email'}
            className={tab === 'email' ? styles.tabActive : styles.tab}
            data-testid="login-tab-email"
            onClick={() => switchTab('email')}
          >
            邮箱登录
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'password'}
            className={tab === 'password' ? styles.tabActive : styles.tab}
            data-testid="login-tab-password"
            onClick={() => switchTab('password')}
          >
            密码登录
          </button>
        </div>

        {tab === 'code' || tab === 'email' ? (
          <form className={styles.form} onSubmit={submitCode}>
            <div className={styles.row}>
              {!isEmail && (
                <select
                  className={styles.countrySelect}
                  data-testid="login-country-code"
                  value={countryCode}
                  onChange={(e) => setCountryCode(Number(e.currentTarget.value))}
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      +{c.code}
                    </option>
                  ))}
                </select>
              )}
              <input
                className={styles.input}
                data-testid="login-code-account"
                placeholder={emailOnly ? '邮箱' : '手机号 / 邮箱'}
                autocomplete={emailOnly ? 'email' : 'username'}
                inputMode={isEmail ? 'email' : 'tel'}
                value={acct}
                onInput={(e) => setAcct(e.currentTarget.value)}
              />
            </div>
            <div className={styles.row}>
              <input
                className={styles.input}
                data-testid="login-sms-code"
                placeholder="验证码"
                autocomplete="one-time-code"
                inputMode="numeric"
                value={smsCode}
                onInput={(e) => setSmsCode(e.currentTarget.value)}
              />
              <button
                type="button"
                className={styles.sendCode}
                data-testid="login-send-code"
                disabled={busy || countdown.seconds > 0 || acct.trim() === ''}
                onClick={requestCode}
              >
                {countdown.seconds > 0 ? `${countdown.seconds}s` : '获取验证码'}
              </button>
            </div>
            {error !== '' && (
              <p className={styles.error} data-testid="login-error">
                {error}
              </p>
            )}
            <button
              className={styles.submit}
              data-testid="login-submit"
              type="submit"
              /* ⚠️ 必须用 acct（按 Tab 取的那一份），不是 codeAccount（21am-12 修）：
                 21t 把验证码/邮箱两个 Tab 的账号拆成各存各的之后，这里漏改——
                 邮箱 Tab 下输入写进 emailAccount，codeAccount 恒空 →
                 账号验证码都填好了，登录键却永久禁用（人工指图实拍）。 */
              disabled={busy || acct.trim() === '' || smsCode.trim() === ''}
            >
              {busy ? '请稍候…' : '登录 / 注册'}
            </button>
          </form>
        ) : (
          <form className={styles.form} onSubmit={submitPassword}>
            <input
              className={styles.input}
              data-testid="login-account"
              placeholder="手机号 / 邮箱 / 用户名"
              autocomplete="username"
              value={account}
              onInput={(e) => setAccount(e.currentTarget.value)}
            />
            <input
              className={styles.input}
              data-testid="login-password"
              type="password"
              placeholder="密码"
              autocomplete="current-password"
              value={password}
              onInput={(e) => setPassword(e.currentTarget.value)}
            />
            {error !== '' && (
              <p className={styles.error} data-testid="login-error">
                {error}
              </p>
            )}
            <button
              className={styles.submit}
              data-testid="login-submit"
              type="submit"
              disabled={busy || account.trim() === '' || password === ''}
            >
              {busy ? '登录中…' : '登录'}
            </button>
            <button
              type="button"
              className={styles.linkButton}
              data-testid="login-forgot"
              onClick={() => {
                setView('forgot');
                setError('');
                setNotice('');
              }}
            >
              忘记密码？
            </button>
          </form>
        )}

        {!props.addAccount && (
          <button
            type="button"
            className={styles.linkButton}
            data-testid="login-qr-entry"
            onClick={() => {
              setView('qr');
              setError('');
              setNotice('');
            }}
          >
            扫码登录
          </button>
        )}

        {/* 海外路线开关（14.39b；旧版 login_page 同位置）：默认域名被墙时的唯一自救口。
            添加账号模式不显示——此时已连上，切路线等于把当前账号也拽走。 */}
        {!props.addAccount && (
          <>
            <button
              type="button"
              className={styles.routeRow}
              role="switch"
              aria-checked={route.view.overseas}
              aria-disabled={route.view.canToggle ? undefined : 'true'}
              data-testid="login-route-switch"
              data-on={route.view.overseas ? 'true' : undefined}
              data-disabled={route.view.canToggle ? undefined : 'true'}
              onClick={route.toggle}
            >
              <span className={styles.routeMain}>
                <span className={styles.routeLabel}>{ROUTE_ROW_LABEL}</span>
                <span className={styles.routeSub} data-testid="login-route-base">
                  {route.view.sub}
                </span>
              </span>
              <span
                className={
                  route.view.overseas
                    ? `${styles.routeSwitch} ${styles.routeSwitchOn}`
                    : styles.routeSwitch
                }
                aria-hidden="true"
              />
            </button>
            {route.error !== '' && (
              <p className={styles.error} data-testid="login-route-error">
                {route.error}
              </p>
            )}
          </>
        )}

        <p className={styles.agreement}>
          登录即表示同意
          <a
            href={`${apiBase}/front/useragree`}
            target="_blank"
            rel="noreferrer"
            data-testid="login-agreement-user"
          >
            《服务协议》
          </a>
          和
          <a
            href={`${apiBase}/front/privacyagree`}
            target="_blank"
            rel="noreferrer"
            data-testid="login-agreement-privacy"
          >
            《隐私政策》
          </a>
        </p>
          </>
        )}
      </div>
      {captchaAppId !== '' && <CaptchaDialog appId={captchaAppId} onDone={onCaptchaDone} />}
    </div>
  );
}
